import React, { FormEvent, useEffect, useRef, useState, useCallback } from 'react';
import { useRouter } from 'next/router';
import styles from './SignUp.module.scss';
import useDictionary from 'lib/hooks/useDictionary';
import { useQueryStringParameters } from 'lib/hooks/browser/useQueryString';
import { FormField } from 'components/helpers/Atoms/FormElements/FormField';
import { TextInput } from 'components/helpers/Atoms/FormElements/TextInput';
import { DateField, DateFormat } from 'components/helpers/Atoms/FormElements/DateField';
import { NewPasswordWithCriteria } from 'components/helpers/Atoms/FormElements/NewPasswordWithCriteria';
import { Checkbox } from 'components/helpers/Atoms/FormElements/Checkbox';
import CtaButton from 'components/helpers/Atoms/Cta/CtaButton';
import { SvgIcon } from 'components/helpers/SvgIcon';
import { BuiltInProviderType } from 'next-auth/providers';
import { RecaptchaBadge } from 'components/helpers/Recaptcha';
import { useCaptchaGeneration } from 'lib/hooks/useCaptchaGeneration';
import { MessageBanner } from 'components/helpers/Atoms/FormElements/MessageBanner';
import { getSession, signIn } from 'next-auth/react';
import { useFormErrors } from 'lib/hooks/useFormErrors';
import { getPasswordRuleStatus, passwordRules } from 'lib/utils/validation/password-validation';
import ExistingUser, { getSocialOAuthDetails, clearSocialOAuthCookie } from './ExistingUser';
import SuccessGate from './SuccessGate';
import { setCookie, removeCookie, getCookie } from 'lib/utils/cookies';
import { DataLayerEvent, pushToDataLayer } from 'components/helpers/Global/Analytics/DataLayer';
import { getDefaultChooseOneValue, isUnselectedQuestionValue } from 'lib/utils/question-options';

const UNDERAGE_COOKIE_NAME = 'NotEligible';
const UNDERAGE_COOKIE_DAYS = 30;

type SignUpScreenState = 'form' | 'success' | 'underage';

type ProfileQuestionOption = {
  label: string;
  value: string;
};

type RegistrationQuestion = {
  QuestionId: string;
  Answer: {
    AnswerId: string;
  };
};

export interface SignUpProps {
  skipCaptcha: boolean;
  headLine: React.ReactNode;
  description: React.ReactNode;
  signInLink: React.ReactNode;
  emailLabel: React.ReactNode;
  emailPlaceholderText: string;
  passwordLabel: React.ReactNode;
  passwordPlaceholderText: string;
  birthdayLabel: React.ReactNode;
  birthdayPlaceholderText: string;
  showBirthday?: boolean;
  zipCodeLabel: React.ReactNode;
  zipCodePlaceholderText: string;
  profileQuestionLabel?: React.ReactNode;
  profileQuestionPlaceholder?: string;
  profileQuestionOptions?: ProfileQuestionOption[];
  showProfileQuestionDropdown?: boolean;
  profileQuestionRequired?: boolean;
  profileQuestionId?: string;
  newsLetterLabel: React.ReactNode;
  newsLetterText: React.ReactNode;
  newsletterSubscriptionId: string;
  legalText: React.ReactNode;
  signupButtonText: React.ReactNode;
  signupSidebarTitle: React.ReactNode;
  signupSidebarDescription: React.ReactNode;
  enableGoogleLogin: boolean;
  enableFacebookLogin: boolean;
  successGateButton: React.ReactNode;
  alreadyMemberHeading?: React.ReactNode;
  alreadyMemberDescription?: React.ReactNode;
  verifyEmailLabel?: React.ReactNode;
  verifyEmailPlaceholder?: string;
  socialConnectSubmitText?: React.ReactNode;
  socialConnectCancelLink?: React.ReactNode;
  forgotPasswordLink?: React.ReactNode;
  sourceCode?: string;
}

const errorMessages: Record<string, string> = {
  EMAIL_ALREADY_EXISTS: 'Components.Register.EmailAlreadyExists',
  INVALID_CAPTCHA: 'Components.Register.InvalidCaptcha',
  REGISTRATION_FAILED: 'Components.Register.RegistrationFailed',
  EMAIL_REQUIRED: 'Components.Register.EmailRequired',
  PASSWORD_REQUIRED: 'Components.Register.PasswordRequired',
  BIRTH_MONTH_YEAR_REQUIRED: 'Components.Register.BirthMonthYearRequired',
  CAPTCHA_LOADING_ERROR: 'Components.Register.CaptchaLoadingError',
  INVALID_EMAIL: 'Components.Register.InvalidEmail',
  INVALID_BIRTHDAY: 'Components.Register.InvalidBirthMonthYear',
  INVALID_ZIP_CODE: 'Components.Register.InvalidZipCode',
  ZIP_CODE_REQUIRED: 'Components.Register.ZipcodeRequired',
  MINIMUM_AGE_REQUIRED: 'Components.Register.MinimumAgeRequired',
  GENERIC_ERROR: 'Components.Register.GenericError',
  EmptySubmissionError: 'Components.Register.EmptySubmissionError',
  BANNER_MESSAGE: 'Components.Register.BannerMessage',
  EMAIL_ALREADY_EXISTS_INLINE_MESSAGE: 'Components.Register.EmailAlreadyExistsInlineMessage',
  // Age gate & success gate
  SIGNUP_AGE_GATE_ERROR: 'Components.Register.signupagegateerror',
  SIGNUP_AGE_GATE_ERROR_MESSAGE: 'Components.Register.successagegateerrormessage',
  SIGNUP_SUCCESS_GATE: 'Components.Register.signupsuccessgate',
  SIGNUP_SUCCESS_GATE_MESSAGE: 'Components.Register.signupsuccessgatemessage',
  SIGNUP_SUCCESS_GATE_BUTTON: 'Components.Register.signupsuccessgatebutton',
  // Profile & Briteverify rule validation errors
  EMAIL_FORMAT: 'Common.LoginAndRegistration.EmailRegistrationResponses.EmailFormat',
  EMAIL_PROFANITY: 'Common.LoginAndRegistration.EmailRegistrationResponses.EmailProfanity',
  FIRST_NAME_CHAR: 'Common.LoginAndRegistration.EmailRegistrationResponses.FirstNameChar',
  FIRST_NAME_PROFANITY: 'Common.LoginAndRegistration.EmailRegistrationResponses.FirstNameProfanity',
  LAST_NAME_CHAR: 'Common.LoginAndRegistration.EmailRegistrationResponses.LastNameChar',
  LAST_NAME_PROFANITY: 'Common.LoginAndRegistration.EmailRegistrationResponses.LastNameProfanity',
  EMAIL_DISPOSABLE: 'Common.LoginAndRegistration.EmailRegistrationResponses.EmailDisposable',
  EMAIL_ACCOUNT_INVALID:
    'Common.LoginAndRegistration.EmailRegistrationResponses.EmailAccountInvalid',
  EMAIL_ADDRESS_INVALID:
    'Common.LoginAndRegistration.EmailRegistrationResponses.EmailAddressInvalid',
  EMAIL_DOMAIN_INVALID: 'Common.LoginAndRegistration.EmailRegistrationResponses.EmailDomainInvalid',
  EMAIL_ROLE_INVALID: 'Common.LoginAndRegistration.EmailRegistrationResponses.EmailRoleInvalid',
  PROFILE_FAIL: 'Common.LoginAndRegistration.EmailRegistrationResponses.ProfileFail',
} as const;

const FORM_FIELD_IDS = {
  EMAIL: 'email',
  PASSWORD: 'password',
  BIRTHDAY: 'birthdate',
  ZIP_CODE: 'zipcode',
  PROFILE_QUESTION: 'profile-question',
  NEWS_LETTER: 'newsLetter',
} as const;

const FORM_FIELD_ERROR_IDS = {
  EMAIL: `${FORM_FIELD_IDS.EMAIL}-error`,
  PASSWORD: `${FORM_FIELD_IDS.PASSWORD}-error`,
  BIRTHDAY: `${FORM_FIELD_IDS.BIRTHDAY}-error`,
  ZIP_CODE: `${FORM_FIELD_IDS.ZIP_CODE}-error`,
  PROFILE_QUESTION: `${FORM_FIELD_IDS.PROFILE_QUESTION}-error`,
} as const;

interface RegistrationError {
  error: string;
  field: string;
}

interface RegistrationResponse {
  error?: string;
  errors?: RegistrationError[];
}

interface OAuthData {
  email: string;
  provider: 'google' | 'facebook';
  providerAccountId: string;
  providerAccessToken: string;
}

const hasUnderageGateCookie = () => getCookie(UNDERAGE_COOKIE_NAME) === 'true';

export default function SignUp(props: SignUpProps) {
  const {
    skipCaptcha,
    headLine,
    description,
    signInLink,
    emailLabel,
    emailPlaceholderText,
    passwordLabel,
    passwordPlaceholderText,
    birthdayLabel,
    birthdayPlaceholderText,
    showBirthday = true,
    zipCodeLabel,
    zipCodePlaceholderText,
    profileQuestionLabel,
    profileQuestionPlaceholder,
    profileQuestionOptions = [],
    showProfileQuestionDropdown = false,
    profileQuestionRequired = false,
    profileQuestionId,
    newsLetterLabel,
    newsLetterText,
    newsletterSubscriptionId,
    legalText,
    signupButtonText,
    signupSidebarTitle,
    signupSidebarDescription,
    enableGoogleLogin,
    enableFacebookLogin,
    successGateButton,
    alreadyMemberHeading,
    alreadyMemberDescription,
    verifyEmailLabel,
    verifyEmailPlaceholder,
    socialConnectSubmitText,
    socialConnectCancelLink,
    forgotPasswordLink,
    sourceCode,
  } = props;
  const { getDictionaryValue } = useDictionary();
  const router = useRouter();
  const [emailParam, esrcParam] = useQueryStringParameters('email', 'esrc');

  // When Sitecore provides the "Choose one" option, use it as the default selection
  // and skip the empty placeholder. Otherwise fall back to the placeholder + validation.
  const defaultProfileQuestionValue = getDefaultChooseOneValue(profileQuestionOptions);

  const [signUpEmail, setSignUpEmail] = useState('');
  const [password, setPassword] = useState('');
  const [birthday, setBirthday] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [profileQuestion, setProfileQuestion] = useState(defaultProfileQuestionValue || '');
  const [isNewsLetterSubscribed, setIsNewsLetterSubscribed] = useState(false);
  const [oauthData, setOauthData] = useState<OAuthData | null>(null);
  const [isExistingUser, setIsExistingUser] = useState(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [signUpError, setSignUpError] = useState('');
  const [screenState, setScreenState] = useState<SignUpScreenState>('form');

  // Write to the sr-announcer div outside <main> so NVDA doesn't prefix with "main landmark"
  useEffect(() => {
    const announcer = document.getElementById('sr-announcer');
    if (!announcer) return;
    announcer.textContent = '';
    if (signUpError) {
      requestAnimationFrame(() => {
        announcer.textContent = signUpError.replace('|', ' ');
      });
    }
  }, [signUpError]);
  const [pendingFocusId, setPendingFocusId] = useState<string | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  // Keeps focus inside <main> while the submit button is disabled so NVDA
  // does not re-announce "main landmark" when focus returns to a form field.
  const formAnchorRef = useRef<HTMLDivElement>(null);

  // WCAG 2.4.3: Move focus to the page heading on mount and on route changes.
  useEffect(() => {
    if (oauthData) return;
    const moveFocus = () => {
      const target = headingRef.current;
      if (!target) return;
      target.setAttribute('tabindex', '-1');
      target.style.outline = 'none';
      target.focus();
      target.addEventListener(
        'blur',
        () => {
          target.removeAttribute('tabindex');
          target.style.outline = '';
        },
        { once: true }
      );
    };
    moveFocus();
    router.events?.on('routeChangeComplete', moveFocus);
    return () => {
      router.events?.off('routeChangeComplete', moveFocus);
    };
  }, [oauthData, router.events]);

  // After OAuth redirect, focus the birthday field since email is pre-filled.
  useEffect(() => {
    if (oauthData) {
      const firstFocusableFieldId = showBirthday
        ? FORM_FIELD_IDS.BIRTHDAY
        : showProfileQuestionDropdown
          ? FORM_FIELD_IDS.PROFILE_QUESTION
          : FORM_FIELD_IDS.ZIP_CODE;
      document.getElementById(firstFocusableFieldId)?.focus();
    }
  }, [oauthData, showBirthday, showProfileQuestionDropdown]);

  // Focus the first error field after the role="alert" banner has had time to announce.
  useEffect(() => {
    if (!pendingFocusId) return;
    const timer = setTimeout(() => {
      document.getElementById(pendingFocusId)?.focus();
      setPendingFocusId(null);
    }, 100);
    return () => clearTimeout(timer);
  }, [pendingFocusId]);

  const { setFieldError, getFieldError, clearAllErrors } = useFormErrors();
  const { captchaToken } = useCaptchaGeneration('signup_captcha', !skipCaptcha);

  const isOauthSignup = oauthData !== null;
  const hideAllSocialLogins = !enableGoogleLogin && !enableFacebookLogin;

  const getEmailValidationError = useCallback(
    (value: string) => {
      const trimmed = value.trim();

      if (!trimmed) {
        return getDictionaryValue(errorMessages.EMAIL_REQUIRED) || 'Email is required';
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(trimmed)) {
        return getDictionaryValue(errorMessages.INVALID_EMAIL) || 'Please enter a valid email';
      }

      return '';
    },
    [getDictionaryValue]
  );

  const getPasswordValidationError = useCallback(
    (value: string) => {
      const trimmed = value.trim();

      if (!trimmed) {
        return getDictionaryValue(errorMessages.PASSWORD_REQUIRED) || 'Password is required';
      }

      const ruleStatus = getPasswordRuleStatus(trimmed, passwordRules);
      const failedRules = passwordRules.filter((rule) => !ruleStatus[rule.id]);

      if (failedRules.length > 0) {
        const passwordMust = getDictionaryValue(
          'Components.MyAccount.PasswordMust',
          'Password must'
        );
        const ruleLabels = failedRules.map((rule) =>
          rule.dictionaryKey ? getDictionaryValue(rule.dictionaryKey, rule.label) : rule.label
        );

        if (ruleLabels.length === 1) {
          return `${passwordMust} ${ruleLabels[0]}.`;
        }

        if (ruleLabels.length === 2) {
          return `${passwordMust} ${ruleLabels[0]} and ${ruleLabels[1]}.`;
        }

        const lastLabel = ruleLabels.pop();
        return `${passwordMust} ${ruleLabels.join(', ')}, and ${lastLabel}.`;
      }

      return '';
    },
    [getDictionaryValue]
  );

  const getBirthdayValidationError = useCallback(
    (value: string) => {
      if (!showBirthday) {
        return '';
      }

      const trimmedValue = value.trim();
      const requiredMessage = getDictionaryValue(
        errorMessages.BIRTH_MONTH_YEAR_REQUIRED,
        'Birth month and year are required.'
      );
      const invalidMessage = getDictionaryValue(
        errorMessages.INVALID_BIRTHDAY,
        'Please enter a valid birth month and year.'
      );

      if (!trimmedValue) {
        return requiredMessage;
      }

      // Expected format: MM/YYYY
      const birthdayRegex = /^(0[1-9]|1[0-2])\/(\d{4})$/;
      const match = trimmedValue.match(birthdayRegex);
      if (!match) {
        return invalidMessage;
      }

      const month = parseInt(match[1], 10);
      const year = parseInt(match[2], 10);
      const currentYear = new Date().getFullYear();

      // Defensive bounds; age validation (minimum age) is handled server-side.
      if (
        Number.isNaN(month) ||
        Number.isNaN(year) ||
        year > currentYear ||
        year < currentYear - 120
      ) {
        return invalidMessage;
      }

      return '';
    },
    [getDictionaryValue, showBirthday]
  );

  const getZipCodeValidationError = useCallback(
    (value: string) => {
      const trimmedValue = value.trim();

      if (!trimmedValue) {
        return getDictionaryValue(errorMessages.ZIP_CODE_REQUIRED) || 'Please enter a zip code.';
      }

      // Zip code must be exactly 5 digits.
      const digitsOnlyRegex = /^\d+$/;
      if (!digitsOnlyRegex.test(trimmedValue) || trimmedValue.length !== 5) {
        return (
          getDictionaryValue(errorMessages.INVALID_ZIP_CODE) || 'Please enter a valid zip code.'
        );
      }

      return '';
    },
    [getDictionaryValue]
  );

  const getProfileQuestionValidationError = useCallback(
    (value: string) => {
      if (!showProfileQuestionDropdown || !profileQuestionRequired) {
        return '';
      }

      if (!isUnselectedQuestionValue(value, profileQuestionRequired)) {
        return '';
      }

      return (
        getDictionaryValue('Components.NewsletterSignup.BusinessIndustryError') ||
        'Please select a business industry'
      );
    },
    [profileQuestionRequired, getDictionaryValue, showProfileQuestionDropdown]
  );

  const showSuccessScreen = useCallback(() => {
    clearSocialOAuthCookie();
    removeCookie('fromSignupPage');
    clearAllErrors();
    setSignUpError('');
    setOauthData(null);
    setIsExistingUser(false);
    setScreenState('success');
  }, [clearAllErrors]);

  const showUnderageScreen = useCallback(
    (persistCookie = true) => {
      if (persistCookie) {
        setCookie(UNDERAGE_COOKIE_NAME, 'true', UNDERAGE_COOKIE_DAYS);
      }

      clearSocialOAuthCookie();
      removeCookie('fromSignupPage');
      clearAllErrors();
      setSignUpError('');
      setOauthData(null);
      setIsExistingUser(false);
      setScreenState('underage');
    },
    [clearAllErrors]
  );

  // Prefill email from query parameter
  useEffect(() => {
    if (emailParam && !signUpEmail && !isOauthSignup) {
      setSignUpEmail(emailParam);
    }
  }, [emailParam, signUpEmail, isOauthSignup]);

  // Initialize error from query params
  useEffect(() => {
    if (router.isReady && router.query.error) {
      if (hasUnderageGateCookie()) {
        return;
      }

      const errorParam = Array.isArray(router.query.error)
        ? router.query.error[0]
        : router.query.error;
      if (errorParam) {
        setSignUpError(errorParam);
      }
    }
  }, [router.isReady, router.query.error]);

  // Restore screen state from query params or persisted age-gate cookie.
  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    if (router.query.registered === 'true') {
      showSuccessScreen();
      return;
    }

    if (hasUnderageGateCookie()) {
      showUnderageScreen(false);
    }
  }, [router.isReady, router.query.registered, showSuccessScreen, showUnderageScreen]);

  // Read OAuth signup information from cookie
  useEffect(() => {
    if (hasUnderageGateCookie()) {
      return;
    }

    const oauthDetails = getSocialOAuthDetails();
    if (oauthDetails) {
      if (oauthDetails.isNewUser) {
        // New user - show minimal signup form with pre-filled OAuth data
        setOauthData({
          email: oauthDetails.email,
          provider: oauthDetails.provider,
          providerAccountId: oauthDetails.providerAccountId,
          providerAccessToken: oauthDetails.idToken,
        });
        // Clear cookie after storing data in state
        clearSocialOAuthCookie();
      } else {
        // Existing user - show social connect flow
        setIsExistingUser(true);
      }
    }
  }, []);

  const handleBirthdayChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setBirthday(e.target.value);
  }, []);

  const handleProfileQuestionChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedValue = e.target.value;
      setProfileQuestion(selectedValue);
      setFieldError(
        FORM_FIELD_IDS.PROFILE_QUESTION,
        getProfileQuestionValidationError(selectedValue)
      );
    },
    [getProfileQuestionValidationError, setFieldError]
  );

  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const emailValue = e.target.value;
      setSignUpEmail(emailValue);

      // Email pattern validation - stricter regex that requires valid domain and TLD
      // Requires: valid local part, @ symbol, valid domain, dot, and TLD with at least 2 letters
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailValue.trim() && !emailRegex.test(emailValue)) {
        setFieldError(
          FORM_FIELD_IDS.EMAIL,
          getDictionaryValue(errorMessages.INVALID_EMAIL) || 'Please enter a valid email address'
        );
      } else {
        // Clear error if email is valid or empty
        setFieldError(FORM_FIELD_IDS.EMAIL, '');
      }
    },
    [getDictionaryValue, setFieldError]
  );

  const handleEmailPaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      // Use setTimeout to ensure the paste value is applied to the input
      setTimeout(() => {
        const target = e.target as HTMLInputElement;
        // Create a synthetic change event to trigger validation
        const syntheticEvent = {
          target: { value: target.value },
        } as React.ChangeEvent<HTMLInputElement>;
        handleEmailChange(syntheticEvent);
      }, 0);
    },
    [handleEmailChange]
  );

  const processRegistrationErrors = useCallback(
    (result: RegistrationResponse) => {
      const hasUnderageError =
        result.error === 'MINIMUM_AGE_REQUIRED' ||
        result.errors?.some((errorObj) => errorObj.error === 'MINIMUM_AGE_REQUIRED');

      if (hasUnderageError) {
        showUnderageScreen();
        return;
      }

      const aggregatedErrors: string[] = [];

      // WCAG 2.4.3: track the first errored field to move focus after the banner announces.
      let firstErrorField: string | null = null;

      const applyEmailAlreadyExistsError = () => {
        setFieldError(
          FORM_FIELD_IDS.EMAIL,
          getDictionaryValue(errorMessages.EMAIL_ALREADY_EXISTS_INLINE_MESSAGE) ||
            'This email address is already registered.'
        );
        setSignUpError(
          getDictionaryValue(errorMessages.EMAIL_ALREADY_EXISTS) ||
            'The email address you entered is already registered. | Sign up with a different email address or log in.'
        );
      };

      if (result.error === 'EMAIL_ALREADY_EXISTS') {
        applyEmailAlreadyExistsError();
        // Move focus to the email field so keyboard/AT users land on the
        // errored control (satisfies WCAG 2.4.3 Focus Order).
        setPendingFocusId(FORM_FIELD_IDS.EMAIL);
        return;
      }

      if (result.errors) {
        result.errors.forEach((errorObj) => {
          if (errorObj.error === 'EMAIL_ALREADY_EXISTS') {
            applyEmailAlreadyExistsError();
            if (!firstErrorField) firstErrorField = FORM_FIELD_IDS.EMAIL;
            return;
          }

          const errorKey = errorMessages[errorObj.error];
          if (errorKey) {
            const errorMessage = getDictionaryValue(errorKey);
            if (errorMessage) {
              setFieldError(errorObj.field, errorMessage);
              aggregatedErrors.push(errorMessage);
              // Record the first errored field for focus management below.
              if (!firstErrorField) firstErrorField = errorObj.field;
            }
          }
        });
      }

      if (aggregatedErrors.length > 0) {
        // Combine banner prefix with field errors, matching the client-side validation format.
        const bannerMessage = getDictionaryValue(errorMessages.BANNER_MESSAGE);
        const summarizedErrors = Array.from(new Set(aggregatedErrors)).join(' ');
        setSignUpError(
          bannerMessage ? `${bannerMessage} ${summarizedErrors}`.trim() : summarizedErrors
        );
      } else if (result.error) {
        // Fallback: no field-specific errors were produced, show generic message.
        setSignUpError(
          getDictionaryValue(errorMessages.GENERIC_ERROR) ||
            'There was an error signing you up.|Please try again.'
        );
      }

      // Move focus to the first errored field (useEffect applies 100 ms delay so banner announces first).
      if (firstErrorField) {
        setPendingFocusId(firstErrorField);
      }
    },
    [getDictionaryValue, setFieldError, setPendingFocusId, showUnderageScreen]
  );

  const callRegistrationAPI = useCallback(
    async (requestParams: Record<string, unknown>): Promise<RegistrationResponse> => {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestParams),
      });
      return response.json();
    },
    []
  );

  const handleAutoLogin = useCallback(
    async (email: string, password: string) => {
      try {
        const loginCaptchaToken = await captchaToken().catch(() => null);
        const signInResponse = await signIn('credentials', {
          username: email,
          password,
          captchaToken: loginCaptchaToken || '',
          redirect: false,
        });

        if (signInResponse?.error) {
          const errorKey = errorMessages[signInResponse.error];
          setSignUpError(
            errorKey && getDictionaryValue(errorKey)
              ? getDictionaryValue(errorKey)
              : getDictionaryValue(errorMessages.GENERIC_ERROR)
          );
        } else {
          // Redirect to thank you page with query parameter
          const currentUrl = new URL(window.location.href);
          currentUrl.searchParams.set('registered', 'true');
          router.push(currentUrl.pathname + currentUrl.search);
        }
      } catch (error) {
        console.error('Auto-login error after registration:', error);
        setSignUpError(getDictionaryValue(errorMessages.GENERIC_ERROR));
      }
    },
    [captchaToken, getDictionaryValue, router]
  );

  const buildCredentialsRequestParams = useCallback(
    (token?: string) => {
      // Priority: URL param → gmi.profile.esrc cookie (set by UTag) → site settings esrcCode
      const resolvedSourceCode =
        esrcParam?.trim() ||
        getCookie('gmi.profile.esrc')?.trim() ||
        sourceCode?.trim() ||
        undefined;
      return {
        email: signUpEmail,
        password,
        birthMonth: showBirthday && birthday ? parseInt(birthday.split('/')[0], 10) : undefined,
        birthYear: showBirthday && birthday ? parseInt(birthday.split('/')[1], 10) : undefined,
        zipCode,
        termsOfUseAcceptance: new Date().toISOString().replace(/\.\d{3}Z$/, 'Z'),
        // pass captchaToken only when captcha is enabled
        ...(skipCaptcha ? {} : { captchaToken: token }),
        ...(resolvedSourceCode && { sourceCode: resolvedSourceCode }),
        ...(isNewsLetterSubscribed && {
          subscriptions: [
            {
              subscriptionId: newsletterSubscriptionId,
              subscribed: isNewsLetterSubscribed,
            },
          ],
        }),
        ...(showProfileQuestionDropdown && profileQuestion && profileQuestionId
          ? {
              questions: [
                {
                  QuestionId: profileQuestionId,
                  Answer: {
                    AnswerId: profileQuestion,
                  },
                },
              ] as RegistrationQuestion[],
            }
          : {}),
        ...(router.locale && { cultureName: router.locale }),
        redirect: false,
        type: 'credentials' as const,
      };
    },
    [
      signUpEmail,
      password,
      birthday,
      zipCode,
      showBirthday,
      esrcParam,
      sourceCode,
      isNewsLetterSubscribed,
      newsletterSubscriptionId,
      showProfileQuestionDropdown,
      profileQuestion,
      profileQuestionId,
      skipCaptcha,
      router.locale,
    ]
  );

  const buildOAuthRequestParams = useCallback(
    (token?: string) => {
      if (!oauthData) {
        throw new Error('OAuth data is missing');
      }
      // Priority: URL param → gmi.profile.esrc cookie (set by UTag) → site settings esrcCode
      const resolvedSourceCode =
        esrcParam?.trim() ||
        getCookie('gmi.profile.esrc')?.trim() ||
        sourceCode?.trim() ||
        undefined;
      return {
        type: 'oauth' as const,
        email: oauthData.email,
        // birthDate: showBirthday ? birthday : undefined,
        birthMonth: showBirthday && birthday ? parseInt(birthday.split('/')[0], 10) : undefined,
        birthYear: showBirthday && birthday ? parseInt(birthday.split('/')[1], 10) : undefined,
        zipCode,
        Federation: {
          Provider: oauthData.provider === 'google' ? 'google' : 'facebook',
          ProviderUserId: oauthData.providerAccountId,
          ProviderUserToken: oauthData.providerAccessToken,
        },
        skipCaptcha,
        // CHANGE: add captchaToken ONLY when captcha is enabled
        ...(skipCaptcha ? {} : { captchaToken: token }),
        ...(resolvedSourceCode && { sourceCode: resolvedSourceCode }),
        ...(isNewsLetterSubscribed && {
          subscriptions: [
            {
              subscriptionId: newsletterSubscriptionId,
              subscribed: isNewsLetterSubscribed,
            },
          ],
        }),
        ...(showProfileQuestionDropdown && profileQuestion && profileQuestionId
          ? {
              questions: [
                {
                  QuestionId: profileQuestionId,
                  Answer: {
                    AnswerId: profileQuestion,
                  },
                },
              ] as RegistrationQuestion[],
            }
          : {}),
        ...(router.locale && { cultureName: router.locale }),
      };
    },
    [
      oauthData,
      birthday,
      zipCode,
      showBirthday,
      esrcParam,
      sourceCode,
      isNewsLetterSubscribed,
      newsletterSubscriptionId,
      showProfileQuestionDropdown,
      profileQuestion,
      profileQuestionId,
      skipCaptcha,
      router.locale,
    ]
  );

  const handleSubmit = async (e: FormEvent, provider: BuiltInProviderType | 'signUpOauth') => {
    e.preventDefault();
    setSignUpError('');
    setScreenState('form');
    formAnchorRef.current?.focus();
    setDisableSubmit(true);
    setIsFormSubmitted(true);
    clearAllErrors();

    try {
      // Handle OAuth provider signup (Google/Facebook)
      if (provider === 'google' || provider === 'facebook') {
        setCookie('fromSignupPage', 'true');

        await signIn(provider, {
          callbackUrl: window.location.pathname + window.location.search,
        });

        return;
      }

      const isOauthCompletion = provider === 'signUpOauth';
      const emailError = isOauthCompletion ? '' : getEmailValidationError(signUpEmail);
      const passwordError = isOauthCompletion ? '' : getPasswordValidationError(password);
      const birthdayError = getBirthdayValidationError(birthday);
      const zipCodeError = getZipCodeValidationError(zipCode);
      const profileQuestionError = getProfileQuestionValidationError(profileQuestion);

      if (emailError || passwordError || birthdayError || zipCodeError || profileQuestionError) {
        const validationErrors = [
          emailError,
          passwordError,
          birthdayError,
          zipCodeError,
          profileQuestionError,
        ].filter(Boolean) as string[];

        if (emailError) {
          setFieldError(FORM_FIELD_IDS.EMAIL, emailError);
        }

        if (passwordError) {
          setFieldError(FORM_FIELD_IDS.PASSWORD, passwordError);
        }

        if (birthdayError) {
          setFieldError(FORM_FIELD_IDS.BIRTHDAY, birthdayError);
        }

        if (zipCodeError) {
          setFieldError(FORM_FIELD_IDS.ZIP_CODE, zipCodeError);
        }

        if (profileQuestionError) {
          setFieldError(FORM_FIELD_IDS.PROFILE_QUESTION, profileQuestionError);
        }

        const bannerMessage = getDictionaryValue(errorMessages.BANNER_MESSAGE);
        const summarizedErrors = Array.from(new Set(validationErrors)).join(' ');
        setSignUpError(
          bannerMessage ? `${bannerMessage} ${summarizedErrors}`.trim() : summarizedErrors
        );

        // Schedule focus on first error field; useEffect fires after React
        // renders the alert banner so the screen reader announces it first.
        const firstErrorFieldId = emailError
          ? FORM_FIELD_IDS.EMAIL
          : passwordError
            ? FORM_FIELD_IDS.PASSWORD
            : birthdayError
              ? FORM_FIELD_IDS.BIRTHDAY
              : zipCodeError
                ? FORM_FIELD_IDS.ZIP_CODE
                : FORM_FIELD_IDS.PROFILE_QUESTION;
        setPendingFocusId(firstErrorFieldId);

        setDisableSubmit(false);
        return;
      }

      // Get captcha token after field validation so field-specific errors are shown first.
      const token = skipCaptcha ? undefined : ((await captchaToken()) ?? undefined);
      if (!skipCaptcha && !token) {
        setSignUpError(getDictionaryValue(errorMessages.GENERIC_ERROR));
        setDisableSubmit(false);
        return;
      }

      // Build request params based on provider type
      const requestParams =
        provider === 'signUpOauth'
          ? buildOAuthRequestParams(token)
          : buildCredentialsRequestParams(token);

      // Call registration API
      const result = await callRegistrationAPI(requestParams);

      // Handle errors
      if (result?.error || result?.errors) {
        setDisableSubmit(false);
        processRegistrationErrors(result);
        return;
      }

      // Handle successful registration
      setSignUpError('');
      const gtmEventPayload: DataLayerEvent = {
        event: 'register',
        page_type: window.utag_data?.page_type || null,
        user_status: window.utag_data?.user_status || null,
        reg_action: 'HEADERJOINLOGIN',
        esrc_code: window.utag_data?.esrc_code || null,
      };
      if (newsletterSubscriptionId && isNewsLetterSubscribed) {
        const newsletterEventPayload: DataLayerEvent = {
          event: 'newsletter_opt_in',
          newsletter_title:
            (newsLetterLabel as { props: { field: { value: string } } })?.props?.field?.value ||
            'newsletter',
          newsletter_id: newsletterSubscriptionId,
          subscriber_position: 'RegFunnelFullPage',
        };
        pushToDataLayer(newsletterEventPayload);
      }
      if (provider === 'credentials') {
        await handleAutoLogin(signUpEmail, password);
        const sessionData = await getSession();
        gtmEventPayload.user_id = sessionData?.user?.id || null;
        pushToDataLayer(gtmEventPayload);
      } else if (provider === 'signUpOauth') {
        // Login directly using the federation token (no re-trigger of OAuth)
        const loginResponse = await signIn('oauth_token', {
          provider: oauthData?.provider,
          userToken: oauthData?.providerAccessToken,
          redirect: false,
        });

        if (loginResponse?.error) {
          console.error('OAuth login after registration failed:', loginResponse.error);

          setSignUpError(getDictionaryValue(errorMessages.GENERIC_ERROR));
          setDisableSubmit(false);
          return;
        }

        // Clear cookies and redirect to thank you page
        clearSocialOAuthCookie();
        removeCookie('fromSignupPage');

        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set('registered', 'true');
        router.push(currentUrl.pathname + currentUrl.search);
      } else {
        const sessionData = await getSession();
        gtmEventPayload.user_id = sessionData?.user?.id || null;
        pushToDataLayer(gtmEventPayload);
        // Redirect to thank you page with query parameter
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set('registered', 'true');
        router.push(currentUrl.pathname + currentUrl.search);
      }
    } catch (error) {
      console.error('SignUp error:', error);
      setSignUpError(getDictionaryValue(errorMessages.REGISTRATION_FAILED));
    } finally {
      setDisableSubmit(false);
    }
  };

  return (
    <div className={styles.signupWrapper} data-component="authorable/shared/userprofile/signup">
      {!isExistingUser && (
        <div className={styles.signupContent}>
          {screenState === 'form' && (
            <div className={styles.formContentContainer}>
              <div className={styles.formContentHeader}>
                <div className={styles.headerContent}>
                  <h1 ref={headingRef} className={styles.formContentHeaderTitle}>
                    {headLine}
                  </h1>
                  <div className={styles.subHeading}>
                    {description}
                    {signInLink}
                  </div>
                </div>
                <hr className={styles.divider} />
              </div>
              <div className={styles.requiredField}>
                {getDictionaryValue('Components.MyAccount.Required') || '* Required'}
              </div>
              {signUpError && (
                <MessageBanner
                  type="error"
                  message={signUpError}
                  role="none"
                  data-testid="signup-error-summary"
                />
              )}
              {/* Hidden focus anchor: receives focus before submit button is disabled so
                  focus never leaves <main>, preventing NVDA from announcing "main landmark" */}
              <div
                ref={formAnchorRef}
                className={styles.formAnchor}
                tabIndex={-1}
                aria-hidden="true"
              />
              <form
                className={styles.formFieldsWrapper}
                noValidate
                onSubmit={(e) => handleSubmit(e, isOauthSignup ? 'signUpOauth' : 'credentials')}
              >
                {isOauthSignup && oauthData && (
                  <div className={styles.oauthEmailDisplay}>
                    <SvgIcon
                      fill="none"
                      icon={oauthData.provider === 'google' ? 'google' : 'facebook'}
                      size="sm"
                    />
                    <span className={styles.oauthEmailDisplayText}>
                      {getDictionaryValue(
                        'Common.LoginAndRegistration.ContinueSigningUpWith',
                        'Continue signing up with'
                      )}{' '}
                      {oauthData.email}
                    </span>
                  </div>
                )}
                <div className={styles.formFieldsContainer}>
                  {!isOauthSignup && (
                    <>
                      <FormField
                        label={emailLabel}
                        labelFor={FORM_FIELD_IDS.EMAIL}
                        error={!!getFieldError(FORM_FIELD_IDS.EMAIL)}
                        required
                      >
                        <TextInput
                          id={FORM_FIELD_IDS.EMAIL}
                          type="email"
                          required
                          value={signUpEmail}
                          onChange={handleEmailChange}
                          onPaste={handleEmailPaste}
                          placeholder={emailPlaceholderText}
                          error={!!getFieldError(FORM_FIELD_IDS.EMAIL)}
                          errorMessage={
                            getFieldError(FORM_FIELD_IDS.EMAIL)
                              ? (getFieldError(FORM_FIELD_IDS.EMAIL) as string)
                              : undefined
                          }
                          errorId={
                            getFieldError(FORM_FIELD_IDS.EMAIL)
                              ? FORM_FIELD_ERROR_IDS.EMAIL
                              : undefined
                          }
                        />
                      </FormField>
                      <NewPasswordWithCriteria
                        id={FORM_FIELD_IDS.PASSWORD}
                        value={password}
                        required
                        onChange={(e) => setPassword(e.target.value)}
                        label={passwordLabel}
                        placeholder={passwordPlaceholderText}
                        showInlineError={true}
                        showLeftIcon={isFormSubmitted}
                        error={
                          isFormSubmitted && getFieldError(FORM_FIELD_IDS.PASSWORD)
                            ? (getFieldError(FORM_FIELD_IDS.PASSWORD) as string)
                            : undefined
                        }
                      />
                    </>
                  )}
                  {showBirthday && (
                    <FormField
                      label={birthdayLabel}
                      labelFor={FORM_FIELD_IDS.BIRTHDAY}
                      error={isFormSubmitted && !!getFieldError(FORM_FIELD_IDS.BIRTHDAY)}
                      required
                    >
                      <DateField
                        id={FORM_FIELD_IDS.BIRTHDAY}
                        value={birthday}
                        onChange={handleBirthdayChange}
                        placeholder={birthdayPlaceholderText}
                        format={'MM/YYYY' as DateFormat}
                        required
                        error={isFormSubmitted && !!getFieldError(FORM_FIELD_IDS.BIRTHDAY)}
                        errorMessage={
                          isFormSubmitted && getFieldError(FORM_FIELD_IDS.BIRTHDAY)
                            ? (getFieldError(FORM_FIELD_IDS.BIRTHDAY) as string)
                            : undefined
                        }
                        errorId={
                          isFormSubmitted && getFieldError(FORM_FIELD_IDS.BIRTHDAY)
                            ? FORM_FIELD_ERROR_IDS.BIRTHDAY
                            : undefined
                        }
                      />
                    </FormField>
                  )}
                  {showProfileQuestionDropdown && (
                    <FormField
                      label={profileQuestionLabel}
                      labelFor={FORM_FIELD_IDS.PROFILE_QUESTION}
                      error={isFormSubmitted && !!getFieldError(FORM_FIELD_IDS.PROFILE_QUESTION)}
                      required={profileQuestionRequired}
                    >
                      <div className={styles.profileQuestionWrapper}>
                        <div
                          className={
                            styles.selectWrapper +
                            (isFormSubmitted && getFieldError(FORM_FIELD_IDS.PROFILE_QUESTION)
                              ? ' ' + styles.selectWrapperError
                              : '')
                          }
                        >
                          <select
                            id={FORM_FIELD_IDS.PROFILE_QUESTION}
                            value={profileQuestion}
                            onChange={handleProfileQuestionChange}
                            required={profileQuestionRequired}
                            disabled={disableSubmit}
                            className={styles.profileQuestionSelect}
                            aria-invalid={
                              isFormSubmitted && !!getFieldError(FORM_FIELD_IDS.PROFILE_QUESTION)
                            }
                            aria-describedby={
                              isFormSubmitted && getFieldError(FORM_FIELD_IDS.PROFILE_QUESTION)
                                ? FORM_FIELD_ERROR_IDS.PROFILE_QUESTION
                                : undefined
                            }
                          >
                            {!defaultProfileQuestionValue && (
                              <option value="">{profileQuestionPlaceholder}</option>
                            )}
                            {profileQuestionOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <span className={styles.selectIcon} aria-hidden="true">
                            <SvgIcon icon="chevron-down" size="sm" />
                          </span>
                        </div>
                        {isFormSubmitted && getFieldError(FORM_FIELD_IDS.PROFILE_QUESTION) && (
                          <p
                            id={FORM_FIELD_ERROR_IDS.PROFILE_QUESTION}
                            className={styles.profileQuestionError}
                            aria-live="polite"
                            aria-atomic="true"
                          >
                            {getFieldError(FORM_FIELD_IDS.PROFILE_QUESTION) as string}
                          </p>
                        )}
                      </div>
                    </FormField>
                  )}
                  <FormField
                    label={zipCodeLabel}
                    labelFor={FORM_FIELD_IDS.ZIP_CODE}
                    error={isFormSubmitted && !!getFieldError(FORM_FIELD_IDS.ZIP_CODE)}
                    required
                  >
                    <TextInput
                      id={FORM_FIELD_IDS.ZIP_CODE}
                      value={zipCode}
                      required
                      onChange={(e) => setZipCode(e.target.value)}
                      placeholder={zipCodePlaceholderText}
                      error={isFormSubmitted && !!getFieldError(FORM_FIELD_IDS.ZIP_CODE)}
                      errorMessage={
                        isFormSubmitted && getFieldError(FORM_FIELD_IDS.ZIP_CODE)
                          ? (getFieldError(FORM_FIELD_IDS.ZIP_CODE) as string)
                          : undefined
                      }
                      errorId={
                        isFormSubmitted && getFieldError(FORM_FIELD_IDS.ZIP_CODE)
                          ? FORM_FIELD_ERROR_IDS.ZIP_CODE
                          : undefined
                      }
                    />
                  </FormField>
                </div>
                <div className={styles.newsLetterSubscriptionWrapper}>
                  <h3 className={styles.newsLetterLabelStyle}>{newsLetterLabel}</h3>
                  <Checkbox
                    id={FORM_FIELD_IDS.NEWS_LETTER}
                    checked={isNewsLetterSubscribed}
                    onChange={(e) => setIsNewsLetterSubscribed(e.target.checked)}
                    label={newsLetterText}
                  />
                </div>
                <div className={styles.legalTextContainer}>{legalText}</div>
                <CtaButton
                  ctaStyle="filled"
                  ctaVariant="brand"
                  type="submit"
                  className={styles.signupButton}
                  disabled={disableSubmit}
                >
                  {signupButtonText}
                </CtaButton>
              </form>

              {!hideAllSocialLogins && !(isOauthSignup && oauthData) && (
                <>
                  <div className={styles.orOptionLine}>
                    <hr className={styles.divider} />
                    <span className={styles.orText}>
                      {getDictionaryValue('Common.LoginAndRegistration.OR', 'OR')}
                    </span>
                    <hr className={styles.divider} />
                  </div>
                  <div className={styles.socialLogins}>
                    {enableGoogleLogin && (
                      <CtaButton
                        ctaStyle="outline"
                        ctaVariant="brand"
                        className={styles.socialButton}
                        onClick={(e) => handleSubmit(e, 'google')}
                      >
                        <SvgIcon fill="none" icon="google" size="sm" />
                        <span>
                          {getDictionaryValue(
                            'Common.LoginAndRegistration.ContinueWithGoogle',
                            'Continue with Google'
                          )}
                        </span>
                      </CtaButton>
                    )}
                    {enableFacebookLogin && (
                      <CtaButton
                        ctaStyle="outline"
                        ctaVariant="brand"
                        className={styles.socialButton}
                        onClick={(e) => {
                          setCookie('fromSignupPage', 'true');
                          handleSubmit(e, 'facebook');
                        }}
                      >
                        <SvgIcon fill="none" icon="facebook" size="sm" />
                        <span>
                          {getDictionaryValue(
                            'Common.LoginAndRegistration.ContinueWithFacebook',
                            'Continue with Facebook'
                          )}
                        </span>
                      </CtaButton>
                    )}
                  </div>
                </>
              )}

              {!skipCaptcha && <RecaptchaBadge />}
            </div>
          )}

          {screenState !== 'form' && (
            <SuccessGate
              title={
                screenState === 'success'
                  ? getDictionaryValue(errorMessages.SIGNUP_SUCCESS_GATE) || 'Success!'
                  : getDictionaryValue(errorMessages.SIGNUP_AGE_GATE_ERROR) || "We're sorry!"
              }
              description={
                screenState === 'success'
                  ? getDictionaryValue(errorMessages.SIGNUP_SUCCESS_GATE_MESSAGE)
                  : getDictionaryValue(errorMessages.SIGNUP_AGE_GATE_ERROR_MESSAGE)
              }
              button={screenState === 'success' ? successGateButton : undefined}
              icon={undefined}
              variant={screenState === 'success' ? 'success' : 'underage'}
              testId={
                screenState === 'success' ? 'signup-success-screen' : 'signup-underage-screen'
              }
            />
          )}

          <div className={styles.signUpSidebar}>
            {signupSidebarTitle && (
              <h2 className={styles.signupSidebarTitle}>{signupSidebarTitle}</h2>
            )}
            {signupSidebarDescription && (
              <div className={styles.signupSidebarDescription}>{signupSidebarDescription}</div>
            )}
          </div>
        </div>
      )}
      {isExistingUser && (
        <ExistingUser
          alreadyMemberHeading={alreadyMemberHeading}
          alreadyMemberDescription={alreadyMemberDescription}
          verifyEmailLabel={verifyEmailLabel}
          verifyEmailPlaceholder={verifyEmailPlaceholder}
          socialConnectSubmitText={socialConnectSubmitText}
          cancelLink={socialConnectCancelLink}
          forgotPasswordLink={forgotPasswordLink}
          passwordLabel={passwordLabel}
          passwordPlaceholderText={passwordPlaceholderText}
        />
      )}
    </div>
  );
}
